"use client";

import * as React from "react";
import { Eraser } from "lucide-react";
import type { Citation } from "@quovy/field-types";
import { Button } from "../inputs/button";
import { Popover, PopoverAnchor, PopoverContent } from "../primitives/popover";
import { CitationList } from "./citations/citation-list";
import { cn } from "../utils";

export interface InlineEditPopoverProps {
  /** Read-only anchor body; must stay mounted whether or not the popover is open. */
  anchor: React.ReactNode;
  /**
   * Render-prop for the inner editor. Receives commit/cancel callbacks
   * that the editor invokes on its own keyboard/input semantics.
   */
  editor: (args: { onCommit: (value: unknown) => void; onCancel: () => void }) => React.ReactNode;
  citations?: Citation[];
  /** Reserved for future registry formatter use. */
  valueType?: string;
  primaryCitationId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit?: (value: unknown) => void;
  onCancel?: () => void;
  /**
   * Optional "clear value" action rendered as the last row of the popover.
   * When provided, a destructive-styled row is appended after the editor
   * (and citation list) that invokes this callback and closes the popover.
   */
  onClear?: () => void;
  onCitationSelect?: (id: string) => void;
  onCitationView?: (id: string) => void;
  align?: "start" | "center" | "end";
  sideOffset?: number;
  className?: string;
}

/**
 * Shared anchored-popover scaffold used by inline edit surfaces (grid cells,
 * detail-page FieldValue). Wraps a read-only anchor and renders an editor +
 * optional citation list inside a Radix Popover portal that carries the
 * `data-field-editor-portal` marker (load-bearing for blur-safe focus on
 * the grid cell — see commit ca9005e8).
 */
export function InlineEditPopover({
  anchor,
  editor,
  citations,
  valueType,
  primaryCitationId,
  open,
  onOpenChange,
  onCommit,
  onCancel,
  onClear,
  onCitationSelect,
  onCitationView,
  align = "start",
  sideOffset = 0,
  className,
}: InlineEditPopoverProps) {
  const handleCommit = React.useCallback(
    (value: unknown) => {
      onCommit?.(value);
      onOpenChange(false);
    },
    [onCommit, onOpenChange],
  );

  const handleCancel = React.useCallback(() => {
    onCancel?.();
    onOpenChange(false);
  }, [onCancel, onOpenChange]);

  const handleClear = React.useCallback(() => {
    onClear?.();
    onOpenChange(false);
  }, [onClear, onOpenChange]);

  // Default citation-row click behavior: commit the citation's snippet as
  // the new value and close. Surfaces that want richer logic (e.g. parsing
  // a snippet through a field-type formatter) can override via the
  // `onCitationSelect` prop and InlineEditPopover will defer to them.
  const handleCitationSelect = React.useCallback(
    (id: string) => {
      if (onCitationSelect) {
        onCitationSelect(id);
        return;
      }
      const match = citations?.find((c) => c.id === id);
      if (match?.snippet !== undefined && match.snippet !== "") {
        handleCommit(match.snippet);
      }
    },
    [onCitationSelect, citations, handleCommit],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel?.();
        onOpenChange(next);
      }}
    >
      <PopoverAnchor asChild>
        <div className="flex min-w-0 flex-1 items-center">{anchor}</div>
      </PopoverAnchor>
      {open && (
        <PopoverContent
          align={align}
          sideOffset={sideOffset}
          className={cn("w-80 p-0", className)}
          // CRITICAL: do NOT rename. The grid's blur-safe focus heuristic
          // checks for this attribute to know an editor portal owns focus.
          data-field-editor-portal
          onOpenAutoFocus={(event) => {
            // Inner editor owns focus (e.g. autoFocus on its input).
            event.preventDefault();
          }}
          onKeyDownCapture={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              handleCancel();
            }
            // Enter is intentionally NOT intercepted here — the inner editor
            // handles input-specific commit semantics.
          }}
        >
          {citations && citations.length > 0 ? (
            // Unified list: editor as the first option, citations beneath.
            // The left-edge accent stripe denotes which row matches the
            // current value (the editor row when no citation does).
            <div className="max-h-80 overflow-y-auto py-1">
              <div
                aria-current={primaryCitationId == null ? "true" : undefined}
                className={cn(
                  "relative flex w-full items-start gap-2 px-3 py-2",
                  "before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r-sm before:content-['']",
                  primaryCitationId == null
                    ? "before:bg-foreground bg-muted/40"
                    : "before:bg-transparent",
                )}
              >
                <div className="min-w-0 flex-1">
                  {editor({ onCommit: handleCommit, onCancel: handleCancel })}
                </div>
              </div>
              <CitationList
                citations={citations}
                primaryCitationId={primaryCitationId ?? null}
                {...(valueType !== undefined ? { valueType } : {})}
                onCitationSelect={handleCitationSelect}
                {...(onCitationView ? { onCitationView } : {})}
              />
            </div>
          ) : (
            <div className="p-2">{editor({ onCommit: handleCommit, onCancel: handleCancel })}</div>
          )}
          {onClear && (
            <div className="border-t">
              <Button
                variant="ghost"
                size="row"
                type="button"
                onClick={handleClear}
                className="gap-2 text-sm text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10"
              >
                <Eraser className="size-3.5" />
                Clear value
              </Button>
            </div>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
}
